import { createValidationEngine } from '@makemydeal/dr-shared-ui-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { getAccessoriesList } from '../selectors/accessoriesSelectors';
import { getVppProducts } from '../selectors/vehicleProtectionSelectors';
import { getShopperInfo, getCoBuyerInfo } from '../selectors/offerRedux';
import * as offerReduxSelectors from '../selectors/offerRedux';
import { getNormalizedTradeInsForValidation } from '../selectors/tradeIns';
import { getHasVehicle, getVehicleVin } from '../selectors/vehicleSelectors';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { trackSaveValidation } from './usabilityTelemetry';

import type { ValidationConfig } from '@makemydeal/dr-shared-ui-utils';

import { customerValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/customerValidationConfig';
import { accessoryValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/accessoryValidationConfig';
import { vehicleProtectionProductValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/vehicleProtectionProductsValidationConfig';
import { outsideLienholderValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/tradeInValidationConfig';
import { incentiveValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/incentiveValidationConfig';
import { sellingVehicleValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/vehicleValidationConfig';

export type SaveValidationInput = {
    buyer?: { email?: string; firstName?: string; lastName?: string; postalCode?: string; [key: string]: any } | null;
    coBuyer?: { email?: string; firstName?: string; lastName?: string; postalCode?: string; [key: string]: any } | null;
    accessories?: Array<{ name?: string; price?: number; cost?: number }> | null;
    incentives?: Array<{ name?: string; amount?: number }> | null;
    tradeIns?: Array<{
        lienholder?: { streetAddress?: string; city?: string; state?: string; zip?: string; [key: string]: any } | null;
        index?: number;
    }> | null;
    vehicleProtectionProducts?: Array<{ productName?: string; price?: number }> | null;
    vehicle?: { hasVehicle?: boolean } | null;
};

export type SaveValidationErrorKeys = keyof SaveValidationInput;

export const saveValidationConfig: ValidationConfig = {
    buyer: { $group: customerValidationConfig, errorPrefix: 'Applicant' },
    coBuyer: { $group: customerValidationConfig, errorPrefix: 'Co-Applicant' },
    accessories: { $collection: accessoryValidationConfig, errorPrefix: 'Accessory' },
    incentives: { $collection: incentiveValidationConfig, errorPrefix: 'Incentive' },
    vehicleProtectionProducts: { $collection: vehicleProtectionProductValidationConfig, errorPrefix: 'Vehicle Protection Product' },
    tradeIns: { $collection: outsideLienholderValidationConfig, errorPrefix: 'Trade' },
    vehicle: { $group: sellingVehicleValidationConfig, errorPrefix: 'Vehicle' }
};

function normalizeSaveValidationInput(state: StateTree): SaveValidationInput {
    const buyerBase = getShopperInfo(state);
    const coBuyerBase = getCoBuyerInfo(state);
    const accessoriesArray = getAccessoriesList(state);
    const productsArray = getVppProducts(state);
    const incentives = offerReduxSelectors.getAppliedIncentives(state);

    const buyer = buyerBase;
    const coBuyer = coBuyerBase;

    const normalizedTradeIns = getNormalizedTradeInsForValidation(state);

    return {
        buyer,
        coBuyer,
        accessories: accessoriesArray && accessoriesArray.length > 0 ? accessoriesArray : null,
        incentives: incentives && incentives.length > 0 ? incentives : null,
        tradeIns: normalizedTradeIns.length > 0 ? normalizedTradeIns : null,
        vehicleProtectionProducts: productsArray && productsArray.length > 0 ? productsArray : null,
        vehicle: { hasVehicle: getHasVehicle(state) }
    };
}

export type RunSaveValidationsOptions = {
    skipValidationKeys?: SaveValidationErrorKeys[];
};

export function runSaveValidations(state: StateTree, options: RunSaveValidationsOptions = {}): any {
    const input = normalizeSaveValidationInput(state);
    const skipValidationKeys = options.skipValidationKeys ?? [];
    const effectiveConfig: ValidationConfig = skipValidationKeys.length
        ? Object.fromEntries(
              Object.entries(saveValidationConfig).filter(([key]) => !skipValidationKeys.includes(key as SaveValidationErrorKeys))
          )
        : saveValidationConfig;
    const engine = createValidationEngine({
        initialConfig: effectiveConfig,
        flow: 'default+save',
        lockFlow: true
    });
    // Populate engine context
    engine.ctx.values = input;
    engine.ctx.offerType = offerReduxSelectors.getCurrentOfferType(state);
    engine.ctx.buyerState = input.buyer?.state ?? null;
    engine.ctx.featureToggles = featureToggleSelectors.getTogglesWithoutSafetyChecks(state) as Record<string, boolean>;
    // Evaluate and return GC-channel form errors
    engine.validateAll();
    const errors = engine.getFormErrorState({ onlyActive: true });
    const hasErrors = engine.getHasFormErrors({ onlyActive: true });

    trackSaveValidation(
        { hasErrors, errors },
        {
            sessionId: offerReduxSelectors.getSessionId(state),
            traceId: offerReduxSelectors.getTraceId(state),
            dealXgId: offerReduxSelectors.getDealXgId(state),
            dealType: offerReduxSelectors.getCurrentOfferType(state),
            mmdDealerId: dealerSelectors.getDealerId(state),
            vin: getVehicleVin(state)
        }
    );

    return { errors, hasErrors };
}
